import { ChangeDetectionStrategy, Component, inject, signal, output, OnInit, input, computed } from '@angular/core';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { GoogleLoginComponent } from '../google-login/google-login.component';
import { AppleLoginComponent } from '../apple-login/apple-login.component';
import { UserApiService } from 'src/app/shared/services/user-api.service';
import { SignupStateService } from '../signup-state.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ReturnUrlService } from 'src/app/shared/services/return-url.service';

@Component({
   selector: 'app-signup',
   templateUrl: './signup.component.html',
   styleUrls: ['./signup.component.scss'],
   imports: [GoogleLoginComponent, AppleLoginComponent, RouterLink, ReactiveFormsModule, NgClass],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignupComponent implements OnInit {
   private router = inject(Router);
   private fb = inject(FormBuilder);
   private userApiService = inject(UserApiService);
   private signupState = inject(SignupStateService);
   private toastr = inject(ToastrService);
   private route = inject(ActivatedRoute);
   private auth = inject(AuthService);
   private returnUrlService = inject(ReturnUrlService);

   isInsidePopup = input<boolean>(false);
   headerText = input<string>("Let's get you started with a Free <strong class=\"tw-font-semibold\">Musaffa</strong> Account");
   subheaderText = input<string>('');
   showFormDirectly = input<boolean>(true);

   isEmailFormVisible = signal(true);
   showPassword = signal(false);
   showConfirmPassword = signal(false);
   isLoading = signal(false);
   errorMessage = signal<string>('');
   emailOtpSent = output<void>();
   loginRequested = output<void>();
   showFormRequested = output<void>();

   private queryParamsSignal = toSignal(this.route.queryParams, { initialValue: {} as any });
   authQueryParams = computed(() => {
      const params = this.queryParamsSignal();
      const filtered: any = {};
      
      // Preserve source if valid
      const source = params['source'];
      if (source === 'trade' || source === 'academy') {
         filtered['source'] = source;
      }

      // Preserve return_url (Crucial for OAuth flow)
      if (params['return_url']) {
         filtered['return_url'] = params['return_url'];
      }

      // Preserve other relevant params
      if (params['redirect']) filtered['redirect'] = params['redirect'];
      if (params['featurebase']) filtered['featurebase'] = params['featurebase'];

      return filtered;
   });

   private passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const password = control.get('password');
      const confirmPassword = control.get('confirmPassword');
      return password && confirmPassword && password.value !== confirmPassword.value ? { passwordMismatch: true } : null;
   };

   step1Form = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.pattern(/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+([a-z]{1,5})\b/)]],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required]
   }, { validators: this.passwordMatchValidator });

   ngOnInit() {
      this.returnUrlService.captureReturnUrl();

      // Set initial visibility based on input
      this.isEmailFormVisible.set(this.showFormDirectly());

      // Pre-fill email if it exists in state (Smart Resumability)
      const cachedEmail = this.signupState.email();
      if (cachedEmail) {
         this.step1Form.patchValue({ email: cachedEmail });
      }
   }

   togglePasswordVisibility() {
      this.showPassword.update(val => !val);
   }

   toggleConfirmPasswordVisibility() {
      this.showConfirmPassword.update(val => !val);
   }

   async submitStep1() {
      if (this.step1Form.invalid) {
         this.step1Form.markAllAsTouched();
         return;
      }

      this.isLoading.set(true);
      this.errorMessage.set('');
      const { email, password } = this.step1Form.getRawValue();

      try {
         if (email && password) {
            const resp: any = await this.userApiService.sendEmailVerificationOtp(email);
            if (resp && resp.status) {
               this.signupState.setSignupData(email, password);
               // Keeping success toast as it's a positive confirmation, 
               // but user specifically asked to move errors into component.
               this.toastr.success('Verification OTP sent to your email');
               
               this.emailOtpSent.emit();
            } else {
               this.errorMessage.set(resp?.message || 'Failed to send OTP. Please try again.');
            }
         }
      } catch (error: any) {
         console.error('Signup step 1 error:', error);
         this.errorMessage.set(error?.error?.message || 'Failed to send OTP. Please try again.');
      } finally {
         this.isLoading.set(false);
      }
   }
   
   handleLoginClick(event: Event) {
      if (this.isInsidePopup()) {
         event.preventDefault();
         event.stopImmediatePropagation();
         this.loginRequested.emit();
      }
   }

   handleLinkClick() {
      if (this.isInsidePopup()) {
         this.auth.closeAuthPopup();
      }
   }

   public toggleEmailForm() {
      this.isEmailFormVisible.set(true);
      this.showFormRequested.emit();
   }
}
